/* eslint-disable @typescript-eslint/no-shadow */
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, inject } from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import { NbDialogService, NbToastrService } from '@nebular/theme';
import { Store } from '@ngxs/store';
import { Observable, Subject, takeUntil } from 'rxjs';
import { WebSocketService } from 'src/app/core/services/insight-web-socket-service';
import { Insight } from 'src/app/models/insight';
import { ACTIONS, STATUSES } from 'src/app/shared/constants/general-constants';
import { NgxGenericModalComponent } from 'src/app/shared/ui/ngx-generic-modal/ngx-generic-modal.component';
import { SharedModule } from '../../../shared/shared.module';
import { InsightsState, LoadListOfFailedJobs, ReloadInsights } from '../redux';
import { LoadInsights, LoadInsightsInactive, ReloadInsightsInactive } from '../redux/insights.actions';
import { InsightService } from '../services/insight.service';
import { DeleteInsightComponent } from './delete-insight/delete-insight.component';
import { EditInsightComponent } from './edit-insight/edit-insight.component';
import { FailedJobInsightListComponent } from './failed-job-insight-list/failed-job-insight-list.component';
import { InsightTerminateModalComponent } from './insight-terminate-modal/insight-terminate-modal.component';
import { StatsInsightComponent } from './stats-insight/stats-insight.component';
import { ViewInsightComponent } from './view-insight/view-insight.component';

@Component({
  selector: 'app-insights',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './insights.component.html',
  styleUrls: ['./insights.component.css'],
  imports: [SharedModule, RouterOutlet]
})
export class InsightsComponent implements OnInit, OnDestroy {
  public activeInsight$: Observable<any[]> = inject(Store).select(InsightsState.insightsActive);
  public inactiveInsight$: Observable<any[]> = inject(Store).select(InsightsState.insightsInactive);
  public loading$: Observable<boolean> = inject(Store).select(InsightsState.insightSpinner);
  public failedJob$: Observable<any[]> = inject(Store).select(InsightsState.failedJobs);
  private unsubscribe: Subject<void> = new Subject();

  public tableData;
  public showActions = [
    ACTIONS.CREATE,
    ACTIONS.EDIT,
    ACTIONS.STATS,
    ACTIONS.START,
    ACTIONS.STOP,
    ACTIONS.CLONE,
    ACTIONS.DELETE
  ];
  public failedInsights;

  constructor(
    private store: Store,
    private dialogService: NbDialogService,
    private socketService: WebSocketService,
    private toastrService: NbToastrService,
    private service: InsightService,
    private router: Router
  ) {
    if (!this.store.selectSnapshot(InsightsState.failedJobs)) {
      this.store.dispatch(LoadListOfFailedJobs);
    }
    if (this.router.url.includes('inactive')) {
      if (!this.store.selectSnapshot(InsightsState.insightsInactive)) {
        this.store.dispatch(LoadInsightsInactive);
      } else {
        this.tableData = this.store.selectSnapshot(InsightsState.insightsInactive);
      }
      this.inactiveInsight$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => (this.tableData = data));
    } else {
      if (!this.store.selectSnapshot(InsightsState.insightsActive)) {
        this.store.dispatch(LoadInsights);
      } else {
        this.tableData = this.store.selectSnapshot(InsightsState.insightsActive);
      }
      this.activeInsight$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => (this.tableData = data));
    }
  }

  public ngOnInit() {
    this.failedJob$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => {
      if (data) {
        this.failedInsights = data
          .filter((job) => job?.type === 'insight')
          .slice()
          .sort(this.sortingByDate);
      }
    });
  }

  public ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  public clickedButton($event) {
    if ($event.action === ACTIONS.REFRESH) {
      this.tableData = undefined;
      if (this.router.url.includes('inactive')) {
        this.store.dispatch(ReloadInsightsInactive);
      } else {
        this.store.dispatch(ReloadInsights);
      }
    }
    if ($event.action === ACTIONS.FAILED_INSIGHTS) {
      this.dialogService.open(FailedJobInsightListComponent, {
        closeOnEsc: false,
        closeOnBackdropClick: false,
        context: {
          failedJobsList: this.failedInsights
        }
      });
    }
    if ($event.action === ACTIONS.CREATE) {
      this.dialogService.open(EditInsightComponent, {
        closeOnEsc: false,
        closeOnBackdropClick: false,
        context: {
          action: $event.action,
          insight: new Insight()
        }
      });
    }
    if ($event.action === ACTIONS.CLONE) {
      const clonedInsight = Insight.clone($event.data);
      clonedInsight.id = null;
      this.dialogService.open(EditInsightComponent, {
        closeOnEsc: false,
        closeOnBackdropClick: false,
        context: {
          action: $event.action,
          insight: clonedInsight
        }
      });
    }
    if ($event.action === ACTIONS.VIEW) {
      const insight: Insight = $event.data;
      this.dialogService.open(ViewInsightComponent, {
        closeOnEsc: false,
        closeOnBackdropClick: false,
        context: {
          insight
        }
      });
    }
    if ($event.action === ACTIONS.STATS) {
      const insight: Insight = $event.data;
      this.dialogService.open(StatsInsightComponent, {
        closeOnEsc: false,
        closeOnBackdropClick: false,
        context: {
          insight
        }
      });
    }
    if ($event.action === ACTIONS.START) {
      const insight: Insight = $event.data;
      if (insight.status === STATUSES.RUNNING) {
        this.toastrService.show(status, $localize`Insight is already running!`, { status: 'danger' });
      } else {
        this.dialogService
          .open(NgxGenericModalComponent, {
            closeOnEsc: false,
            closeOnBackdropClick: false,
            context: {
              title: $localize`Are you sure you want to "Start Insight"? `,
              message: insight.id.toString()
            }
          })
          .onClose.subscribe((data) => {
            if (data) {
              this.socketService.setUp();
              this.service
                .runInsightJob(insight)
                .pipe(takeUntil(this.unsubscribe))
                .subscribe((data) => {
                  setTimeout(() => {
                    this.socketService.sendMessage(data.job_id, insight.id);
                  }, 2000);
                });
            }
          });
      }
    }
    if ($event.action === ACTIONS.STOP) {
      const insight: Insight = $event.data;
      if (insight.status !== STATUSES.RUNNING) {
        this.toastrService.show(status, $localize`Insight is not running!`, { status: 'danger' });
      } else {
        this.dialogService.open(InsightTerminateModalComponent, {
          closeOnEsc: false,
          closeOnBackdropClick: false,
          context: {
            title: $localize`Are you sure you want to "Terminate": `,
            message: insight.id.toString(),
            job_id: insight.batch_job_id,
            insight_name: insight.name
          }
        });
      }
    }
    if ($event.action === ACTIONS.EDIT) {
      const insight: Insight = $event.data;
      if (insight.status === STATUSES.RUNNING) {
        this.toastrService.show(status, $localize`Cannot edit a running insight`, { status: 'danger' });
      } else {
        this.dialogService.open(EditInsightComponent, {
          closeOnEsc: false,
          closeOnBackdropClick: false,
          context: {
            action: $event.action,
            insight: Insight.copy(insight)
          }
        });
      }
    }
    if ($event.action === ACTIONS.DELETE) {
      const insight: Insight = $event.data;
      this.dialogService.open(DeleteInsightComponent, {
        closeOnEsc: false,
        closeOnBackdropClick: false,
        context: {
          insight: Insight.copy(insight) as Insight
        }
      });
    }
  }

  public sortingByDate(a, b) {
    if (a.date_created > b.date_created) {
      return -1;
    }
    if (a.date_created < b.date_created) {
      return 1;
    }

    return 0;
  }
}
